import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SiteSettings } from '../types';

interface FillersAntwerpenProps {
  settings: SiteSettings;
}

const FillersAntwerpen: React.FC<FillersAntwerpenProps> = ({ settings }) => {
  return (
    <div className="bg-theme-bg pt-32 pb-24 transition-colors">
      <Helmet>
        <title>Fillers Antwerpen | Natuurlijke Gezichtsbalans bij Dr. An De Cuyper</title>
        <meta name="description" content="Fillers in Antwerpen voor natuurlijk herstel van volume, contour en gezichtsbalans. Persoonlijk medisch advies door Dr. An De Cuyper." />
        <link rel="canonical" href="https://andecuyper.be/fillers-antwerpen" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                "@id": "https://andecuyper.be/fillers-antwerpen#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://andecuyper.be"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Fillers Antwerpen",
                    "item": "https://andecuyper.be/fillers-antwerpen"
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "@id": "https://andecuyper.be/fillers-antwerpen#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Wat is het verschil tussen fillers en Botox?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Botox (botulinetoxine) rimpeltjes ontstaan door spieractiviteit te verminderen; Botox ontspant dus de spieren. Dermale fillers daarentegen herstellen volume, ondersteunen gelaatscontouren en vullen statische rimpels op."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Welke zones kunnen met fillers behandeld worden?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Fillers kunnen gebruikt worden voor het herstellen en ondersteunen van volume in zones zoals de jukbeenderen, kaaklijn, kin, neus-lippenplooi, en bij de lippen."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Hoe natuurlijk kan een fillerbehandeling eruitzien?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Bij een uiterst conservatieve dosering en oog voor gezichtsverhoudingen kan een fillerbehandeling een zeer discreet, fris en natuurlijk resultaat geven dat uw gezichtsbalans herstelt."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Zijn fillers permanent?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Nee, bij MD An De Cuyper gebruiken we uitsluitend tijdelijke hyaluronzuur fillers. Deze worden binnen 6 tot 18 maanden geleidelijk en op natuurlijke wijze door het lichaam afgebroken."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Kan ik eerst advies krijgen over welke behandeling past?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Absoluut. Elke behandeling start met een persoonlijk consult en een uitgebreid gezichtsanalysetraject om te ontdekken of en welke fillers aansluiten bij uw wensen."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <section className="max-w-4xl mx-auto px-6">
        <span className="text-primary text-xs font-black uppercase tracking-[0.4em] mb-4 block">Gespecialiseerde Behandeling</span>
        <h1 className="font-display text-5xl md:text-7xl text-theme-text leading-tight mb-12">
          Fillers in Antwerpen voor volume, contour en natuurlijke gezichtsbalans
        </h1>

        <div className="prose prose-lg prose-neutral dark:prose-invert max-w-none text-theme-muted font-light leading-relaxed space-y-12">
          <section className="space-y-4">
            <p className="text-2xl text-theme-text font-medium leading-normal italic border-l-4 border-primary pl-8">
              Herontdek uw frisse uitstraling op een veilige en medisch verantwoorde manier. Dr. An De Cuyper gebruikt in haar praktijk te Antwerpen hoogwaardige tijdelijke hyaluronzuur fillers om diepte en volume in uw gelaat op een subtiele manier te herstellen en uw natuurlijke gezichtsbalans te ondersteunen.
            </p>
            <p>
              Dermale fillers zijn injectable medisch-esthetische behandelingen die gericht volume en structuur kunnen geven aan het gezicht. Door veroudering of aanleg kan het gelaat volume verliezen, wat meestal leidt tot een vermoeide of minder vitale uitstraling. Onder deskundige medische leiding van Dr. An De Cuyper zorgen we voor veilige en elegante verfijning.
            </p>
            <div className="pt-4">
              <a 
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all inline-block"
              >
                Plan een persoonlijk advies
              </a>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Wanneer kunnen fillers geschikt zijn?</h2>
            <p>
              Elk gezicht is uniek en verandert in de loop der jaren op een eigen manier. Een behandeling met hyaluronzuur fillers kan een geschikte oplossing zijn bij diverse esthetische wensen of indicaties:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-theme-muted">
              <li><strong>Volumeverlies:</strong> het opvullen van zones waar het vetweefsel of de botmassa met de jaren is afgenomen.</li>
              <li><strong>Vermoeide uitstraling:</strong> het verfrissen van de gezichtsuitdrukking door schaduwen te verminderen.</li>
              <li><strong>Minder scherpe contouren:</strong> het terugbrengen van definitie in de jukbeenderen, kaaklijn of kin.</li>
              <li><strong>Neus-lippenplooi:</strong> het subtiel verzachten van de diepere statische lijnen naast de mond.</li>
              <li><strong>Jukbeenderen:</strong> herstel van volume voor een milde lift en meer structuur.</li>
              <li><strong>Kaaklijn:</strong> het accentueren van de contour voor een jeugdiger en strakker profiel.</li>
              <li><strong>Kin:</strong> het verfijnen van de kinprojectie ten opzichte van de neus en de lippen om gelaatsharmonie te herstellen.</li>
              <li><strong>Asymmetrie:</strong> het discreet corrigeren van oneffenheden om de natuurlijke gezichtsbalans te bevorderen.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Behandelzones voor fillers</h2>
            <p>
              In onze praktijk in de regio Antwerpen kunnen we diverse zones van het gezicht uiterst gericht en veilig behandelen door het gebruik van verschillende densiteiten hyaluronzuur fillers:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 not-prose mt-6">
              <div className="bg-theme-surface p-6 rounded-2xl border border-theme-border">
                <h4 className="font-bold text-lg text-theme-text mb-2">Jukbeenderen</h4>
                <p className="text-sm text-theme-muted font-light">
                  Het terugbrengen van verloren gegaan volume op de jukbeenderen helpt overige gezichtszones subtiel omhoog te trekken (meestal 'liquid facelift' genoemd), wat de vermoeidheid uit het gezicht wegneemt.
                </p>
              </div>
              <div className="bg-theme-surface p-6 rounded-2xl border border-theme-border">
                <h4 className="font-bold text-lg text-theme-text mb-2">Kaaklijn & Kin</h4>
                <p className="text-sm text-theme-muted font-light">
                  Voor het creëren van een helder gedefinieerde kaaklijn en een krachtige kinprojectie, hetgeen bijdraagt aan een harmonieus profiel.
                </p>
              </div>
              <div className="bg-theme-surface p-6 rounded-2xl border border-theme-border">
                <h4 className="font-bold text-lg text-theme-text mb-2">Neus-lippenplooi</h4>
                <p className="text-sm text-theme-muted font-light">
                  De plooien tussen neusvleugels en mondhoeken kunnen met een zachte hyaluronzuur filler vloeiend worden opgevuld om de schaduwen te matigen.
                </p>
              </div>
              <div className="bg-theme-surface p-6 rounded-2xl border border-theme-border">
                <h4 className="font-bold text-lg text-theme-text mb-2">Lippen</h4>
                <p className="text-sm text-theme-muted font-light">
                  Voor specifieke behandelingen van de lippen, zoals het verfijnen van de lipcontour of subtiel lip volume, verwijzen we naar onze toegewijde pagina <a href="/lippen-opvullen-antwerpen" className="text-primary underline hover:text-white transition-colors">Lippen Opvullen Antwerpen</a>.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Fillers versus Botox</h2>
            <p>
              Het is belangrijk om het functionele verschil tussen Botox en fillers te begrijpen. <a href="/botox-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Botox Antwerpen</a> (botulinetoxine) is een spierontspanner die de onderliggende spieractiviteit die dynamische rimpels veroorzaakt (zoals fronsrimpels, voorhoofdslijnen en kraaienpootjes) tijdelijk vermindert.
            </p>
            <p>
              Dermale fillers daarentegen ontspannen geen spieren, maar herstellen verloren volume en ondersteunen de specifieke contouren en structuur van het gelaat. Afhankelijk van de anatomie en uw specifieke wensen kan een combinatie van beide behandelingen worden besproken tijdens de persoonlijke consultatie om optimale gezichtsbalans te verkrijgen.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Natuurlijke gezichtsbalans</h2>
            <p>
              De visie van Dr. An De Cuyper is gebaseerd op het behoud van uw eigenheid. We geloven niet in het veranderen van uw gezichtsuitdrukkingen of het creëren van een onnatuurlijk gezwollen look. Door conservatieve doseringen te hanteren en een nauwgezette anatomische analyse uit te voeren, wordt overcorrectie te allen tijde vermeden.
            </p>
            <p>
              Een goed opgesteld behandelplan houdt rekening met de gezichtsverhoudingen en focust op subtiele, geleidelijke correcties in plaats van een plotse metamorfose. Uw omgeving zal merken dat u er frisser en beter uitgerust uitziet, zonder de vinger op een behandeling te kunnen leggen.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Veiligheid en productkeuze</h2>
            <p>
              Bij MD An De Cuyper staat medische veiligheid onvoorwaardelijk centraal. De keuze van de specifieke filler en de benodigde hoeveelheid is maatwerk en hangt nauw samen met uw anatomie, de huidkwaliteit, de behandelzone en uw persoonlijke esthetische wensen.
            </p>
            <p>
              Al onze behandelingen worden uitsluitend uitgevoerd door een universitair opgeleide en ervaren arts. De keuze van product, techniek en hoeveelheid wordt zorgvuldig afgestemd op de behandelde zone, de anatomie en het gewenste natuurlijke resultaat. Sommige hyaluronzuurfillers zijn in bepaalde situaties corrigeerbaar, maar elke fillerbehandeling blijft een medische handeling met mogelijke risico’s en bijwerkingen. Er kunnen na de behandeling tijdelijke, milde bijwerkingen optreden zoals lokale roodheid, lichte zwelling, gevoeligheid of een blauwe plek op de injectieplaats. Deze effecten verdwijnen meestal snel. Eventuele zeldzame complicaties worden uitvoerig voorafgaand aan de behandeling met u besproken.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Nazorg na fillers</h2>
            <p>
              Direct na de filler behandeling adviseren we u de richtlijnen van onze kliniek nauwlettend op te volgen. Vermijd onnodige druk, wrijven of manipulatie van het behandelde gebied gedurende de eerste dagen, tenzij u hiertoe specifieke instructies krijgt van de arts.
            </p>
            <p>
              Wij adviseren om gedurende 24 tot 48 uur na de behandeling geen heftige sporten te beoefenen, alcohol te consumeren of gebruik te maken van de sauna of zonnebank om zwelling of blauwe plekken minimaal te houden. Mocht u onverwachte symptomen opmerken, kunt u ons altijd direct bereiken.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Fillers in Antwerpen, Edegem en Hoboken</h2>
            <p>
              Onze hoogwaardige fillerbehandelingen worden aangeboden onder leiding van Dr. An De Cuyper op onze locaties in Antwerpen (Clinic 2650), Edegem, Hoboken en Burcht. Zo kunnen we een vlot bereikbare, medisch verantwoorde omgeving bieden aan cliënten uit de brede Antwerpse regio. Al onze behandelingen en bijbehorende tarieven zijn transparant in te zien op onze pagina met <a href="/tarieven" className="text-primary underline hover:text-white transition-colors font-medium">tarieven</a>.
            </p>
          </section>

          <section className="space-y-6 pt-8 not-prose">
            <h2 className="text-3xl font-display text-theme-text font-semibold">Veelgestelde Vragen over Fillers</h2>
            <div className="space-y-4">
              <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                  <span>Wat is het verschil tussen fillers en Botox?</span>
                  <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                </summary>
                <p className="mt-4 text-theme-muted font-light text-sm">
                  Botox (botulinetoxine) rimpeltjes ontstaan door spieractiviteit te verminderen; Botox ontspant dus de spieren. Dermale fillers daarentegen ontspannen geen spieren, maar herstellen verloren volume, ondersteunen gelaatscontouren en vullen statische rimpels op door gebruik te maken van natuurlijk hyaluronzuur.
                </p>
              </details>
              <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                  <span>Welke zones kunnen met fillers behandeld worden?</span>
                  <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                </summary>
                <p className="mt-4 text-theme-muted font-light text-sm">
                  Dermale fillers kunnen worden ingezet in een scala aan gelaatszones, waaronder de jukbeenderen, kaaklijn, kin en de neus-lippenplooi. Voor de specifieke behandeling van lippen verwijzen wij u naar onze pagina over <a href="/lippen-opvullen-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Lippen Opvullen Antwerpen</a>.
                </p>
              </details>
              <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                  <span>Hoe natuurlijk kan een fillerbehandeling eruitzien?</span>
                  <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                </summary>
                <p className="mt-4 text-theme-muted font-light text-sm">
                  Bij een conservatieve dosering door een gekwalificeerde arts geven fillers een uiterst verfrissend, subtiel en natuurlijk resultaat. De contouren en de gezichtsbalans worden ondersteund zonder dat uw gezichtsvoorwaarden of expressie wezenlijk veranderen.
                </p>
              </details>
              <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                  <span>Zijn fillers permanent?</span>
                  <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                </summary>
                <p className="mt-4 text-theme-muted font-light text-sm">
                  Nee. In onze kliniek van Dr. An De Cuyper herstellen we volume uitsluitend via tijdelijke fillers op basis van hyaluronzuur. Deze biologische verbindingen worden in de loop van 6 tot 18 maanden vanzelf door het lichaam op een veilige en natuurlijke manier afgebroken.
                </p>
              </details>
              <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                  <span>Kan ik eerst advies krijgen over welke behandeling past?</span>
                  <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                </summary>
                <p className="mt-4 text-theme-muted font-light text-sm">
                  Absoluut. Wij geloven in zorgvuldige en gerichte begeleiding. Tijdens een persoonlijk en vrijblijvend consult analyseren we uw gelaat en bespreken we samen de behandelmogelijkheden die het best aansluiten bij uw huid, anatomie en gewenste doelen.
                </p>
              </details>
            </div>
          </section>

          <div className="mt-16 bg-background-dark p-12 rounded-[3rem] text-center text-white border border-white/5 not-prose">
            <h3 className="font-display text-3xl mb-6">Wilt u meer volume of definitie?</h3>
            <p className="mb-10 text-gray-400 max-w-xl mx-auto">
              Plan vandaag nog uw consultatie voor fillers bij Dr. An De Cuyper en ervaar het verschil van medische expertise.
            </p>
            <a 
              href={settings.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-black px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all inline-block"
            >
              Maak een Afspraak
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FillersAntwerpen;
